/**
 * 관계 파일 입출력.
 *
 * JSON 형식은 offline-manage 콘솔의 bulk API(rows) 와 같다 — 여기서 내려받은
 * 파일을 현장 콘솔의 "관계 올리기" 에 그대로 넣어 DB 에 반영한다.
 * SQL 은 콘솔에 접근할 수 없는 현장에서 mysql 로 직접 넣을 때만 쓴다.
 */

import { isValidCctvName, isValidDirection, MAX_DEPTH, type CctvConnection } from '../model/types'

/** 내려받는 파일 본문. connections 키는 현장 콘솔이 읽는 이름 그대로다. */
export function toRelationJson(rows: CctvConnection[]): string {
  return `${JSON.stringify({ connections: rows }, null, 2)}\n`
}

/**
 * 파일에서 읽은 JSON 을 검증하며 행으로 바꾼다. 한 줄이라도 규칙을 벗어나면
 * 전체를 거부한다 — 절반만 반영된 관계는 점등이 어디서 끊겼는지 알 수 없다.
 */
export function parseRelationJson(text: string): CctvConnection[] {
  let raw: unknown
  try {
    raw = JSON.parse(text)
  } catch (e) {
    throw new Error(`JSON 형식이 아닙니다: ${(e as Error).message}`)
  }
  const list = Array.isArray(raw) ? raw : (raw as { connections?: unknown })?.connections
  if (!Array.isArray(list)) {
    throw new Error('connections 배열이 없습니다')
  }
  const rows: CctvConnection[] = []
  list.forEach((item, i) => {
    const r = item as Partial<CctvConnection>
    const at = `${i + 1}번째 행`
    const name = String(r.name ?? '')
    const to = String(r.to ?? '')
    const direction = String(r.direction ?? '')
    const depth = Number(r.depth ?? 1)
    if (!isValidCctvName(name)) throw new Error(`${at}: name 이 잘못됐습니다 (${name})`)
    if (!isValidCctvName(to)) throw new Error(`${at}: to 가 잘못됐습니다 (${to})`)
    if (to === name) throw new Error(`${at}: 자기 자신을 이웃으로 넣을 수 없습니다 (${name})`)
    if (!isValidDirection(direction)) {
      throw new Error(`${at}: direction 은 left 또는 right 여야 합니다 (${direction})`)
    }
    if (!Number.isInteger(depth) || depth < 1 || depth > MAX_DEPTH) {
      throw new Error(`${at}: depth 는 1~${MAX_DEPTH} 정수여야 합니다 (${r.depth})`)
    }
    rows.push({ name, to, direction, depth })
  })
  return dedupe(rows)
}

/** (name,to,direction) 중복 제거 — 같은 이웃을 두 번 넣어도 DB 에는 한 행이면 된다. */
export function dedupe(rows: CctvConnection[]): CctvConnection[] {
  const seen = new Set<string>()
  const out: CctvConnection[] = []
  for (const r of rows) {
    const key = `${r.name} ${r.to} ${r.direction}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push(r)
  }
  return out
}

/**
 * 현장에서 mysql 로 직접 넣을 때 쓰는 전체 교체 SQL. 이름은 검증된 문자집합
 * (영문/숫자/'.'/'_'/'-')이라 따옴표 안에 그대로 넣어도 안전하다.
 */
export function toRelationSql(rows: CctvConnection[]): string {
  const lines = [
    '-- CCTV 관계(cctv_connections) 전체 교체 — 맵 에디터 생성본',
    'START TRANSACTION;',
    'DELETE FROM cctv_connections;',
  ]
  if (rows.length > 0) {
    const values = rows
      .map((r) => `('${r.name}','${r.to}','${r.direction}',${r.depth})`)
      .join(',\n  ')
    lines.push(
      `INSERT INTO cctv_connections (name, to_cctv_id, direction, depth) VALUES\n  ${values};`,
    )
  }
  lines.push('COMMIT;')
  return `${lines.join('\n')}\n`
}
